import {
  ReviewEdgeFragment,
  isNative,
  isWeb,
  useGetAverageReviewsForApplicationQuery,
  useListReviewsQuery,
} from "@stepapp/core";
import { ReviewSortFields, SortDirection } from "@stepapp/core/apiTypes";
import { Block } from "@stepapp/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import FloatingButton from "./FloatingButton";
import ReviewsList from "./ReviewsList";

const itemsToFetch = 9;

const ReviewsScreen = () => {
  const footerRef = useRef(null);
  const [pagination, setPagination] = useState({ hasNextPage: false, endCursor: undefined });
  const [reviewItems, setReviewItems] = useState<ReviewEdgeFragment[]>([]);

  const { data: avgReviews } = useGetAverageReviewsForApplicationQuery();
  const { loading, fetchMore } = useListReviewsQuery({
    variables: {
      filter: { rating: { gte: 1 } },
      paging: { first: itemsToFetch },
      sorting: {
        direction: SortDirection.Desc,
        field: ReviewSortFields.ReservationStartedAt,
      },
    },
    onCompleted(data) {
      const newReviews = data?.reviews?.edges || [];
      setPagination({
        endCursor: data?.reviews?.pageInfo?.endCursor,
        hasNextPage: !!data?.reviews?.pageInfo?.hasNextPage,
      });
      setReviewItems((prevState) => [...prevState, ...newReviews]);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (isNative) return;

    const footerElement = footerRef.current;
    const newObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextReviews();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      },
    );

    if (footerElement) {
      newObserver.observe(footerElement);
    }

    return () => {
      if (footerElement) {
        newObserver.unobserve(footerElement);
      }
    };
  }, [pagination]);

  const fetchNextReviews = useCallback(async () => {
    if (!(pagination.hasNextPage && pagination.endCursor)) return;

    const { data: newData } = await fetchMore({
      variables: {
        filter: { rating: { gte: 1 } },
        paging: { first: itemsToFetch, after: pagination.endCursor },
        sorting: {
          direction: SortDirection.Desc,
          field: ReviewSortFields.ReservationStartedAt,
        },
      },
    });

    const newReviews = newData?.reviews?.edges || [];

    setReviewItems((prevState) => [...prevState, ...newReviews]);
    setPagination((prevState) => ({
      ...prevState,
      endCursor: newData?.reviews?.pageInfo?.endCursor,
    }));
  }, [fetchMore, pagination]);

  const handleOnEndReached = useCallback(() => {
    if (isWeb) return;

    fetchNextReviews();
  }, [fetchNextReviews]);

  return (
    <Block style={{ flex: 1 }}>
      <ReviewsList
        reviewItems={reviewItems}
        loading={loading}
        avgAppReviews={avgReviews?.getAverageReviewsForApplication}
        handleOnEndReached={handleOnEndReached}
        footerRef={footerRef}
      />

      <FloatingButton
        rating={avgReviews?.getAverageReviewsForApplication.average}
        numberOfReviews={avgReviews?.getAverageReviewsForApplication.total}
      />
    </Block>
  );
};

export default ReviewsScreen;
